import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldOff,
  Copy,
  Check,
  Download,
  X,
  KeyRound,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

/**
 * v1.2 PR-B.3 — Two-factor authentication card (AccountProfile).
 *
 *  - GET  /auth/me/mfa/status
 *  - POST /auth/me/mfa/enroll/start
 *  - POST /auth/me/mfa/enroll/verify (TOTP)
 *  - POST /auth/me/mfa/disable (password + TOTP/backup)
 *  - POST /auth/me/mfa/regenerate-backup-codes (password + TOTP)
 *
 * MFA is opt-in. Backup codes are shown ONCE and ONLY ONCE; users must save
 * them at enrollment time. Regeneration replaces them all.
 */
export default function MfaCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(null); // "enroll" | "disable" | "regen"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me/mfa/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      className="rounded-2xl border border-navy-100 dark:border-navy-700 bg-white dark:bg-navy-800 p-6"
      data-testid="mfa-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-navy-900 dark:text-white inline-flex items-center gap-2">
            {status?.mfa_enabled ? (
              <ShieldCheck className="w-4 h-4 text-success" />
            ) : (
              <ShieldOff className="w-4 h-4 text-navy-400 dark:text-navy-300" />
            )}
            Two-factor authentication
          </h2>
          <p className="text-xs text-navy-500 dark:text-navy-300 mt-1">
            Adds a second factor on top of your password using an authenticator app like 1Password,
            Authy, or Google Authenticator.
          </p>
        </div>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
            status?.mfa_enabled
              ? "bg-success/10 text-success border-success/30"
              : "bg-navy-50 dark:bg-navy-700 text-navy-500 dark:text-navy-300 border-navy-200 dark:border-navy-600"
          }`}
          data-testid="mfa-status-pill"
        >
          {status?.mfa_enabled ? "ON" : "OFF"}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-navy-500 dark:text-navy-300">Loading…</div>
      ) : status?.mfa_key_loaded === false ? (
        <div className="text-sm bg-warning/10 border border-warning/30 text-warning rounded-lg p-3 inline-flex gap-2 items-start" data-testid="mfa-unavailable">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          MFA is currently unavailable. The platform operator must set the
          <code className="font-mono mx-1">MFA_SECRETS_KEY</code> environment variable.
        </div>
      ) : status?.mfa_enabled ? (
        <EnabledView
          status={status}
          onDisable={() => setOpenModal("disable")}
          onRegenerate={() => setOpenModal("regen")}
        />
      ) : (
        <DisabledView onEnable={() => setOpenModal("enroll")} />
      )}

      {openModal === "enroll" && (
        <EnrollModal onClose={() => { setOpenModal(null); load(); }} />
      )}
      {openModal === "disable" && (
        <DisableModal onClose={() => { setOpenModal(null); load(); }} />
      )}
      {openModal === "regen" && (
        <RegenerateModal onClose={() => { setOpenModal(null); load(); }} />
      )}
    </div>
  );
}

function DisabledView({ onEnable }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-navy-600 dark:text-navy-200">
        Two-factor authentication is <strong>off</strong>. Strongly recommended for org_admin
        and super_admin accounts.
      </p>
      <button
        onClick={onEnable}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600"
        data-testid="mfa-enable-btn"
      >
        <ShieldCheck className="w-3.5 h-3.5" /> Enable two-factor
      </button>
    </div>
  );
}

function EnabledView({ status, onDisable, onRegenerate }) {
  const { formatDateTime } = useLocale();
  return (
    <div className="space-y-3">
      <div className="text-sm text-navy-700 dark:text-navy-100">
        Enabled on <strong>{status?.mfa_enabled_at ? formatDateTime(status.mfa_enabled_at) : "—"}</strong>.
      </div>
      <div className="text-sm text-navy-700 dark:text-navy-100" data-testid="mfa-backup-remaining">
        <strong>{status?.backup_codes_remaining ?? 0}</strong> backup code(s) remaining.
        {(status?.backup_codes_remaining ?? 0) <= 2 && (
          <span className="ml-2 text-warning text-xs">⚠ Regenerate soon.</span>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={onRegenerate}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-navy-200 dark:border-navy-600 text-navy-700 dark:text-navy-100 hover:bg-navy-50 dark:hover:bg-navy-700"
          data-testid="mfa-regen-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Regenerate backup codes
        </button>
        <button
          onClick={onDisable}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-danger/30 text-danger hover:bg-danger/5"
          data-testid="mfa-disable-btn"
        >
          <ShieldOff className="w-3.5 h-3.5" /> Disable
        </button>
      </div>
    </div>
  );
}

// ---------------- Enrollment ---------------------------------------------

function EnrollModal({ onClose }) {
  const [step, setStep] = useState("start");        // "start" | "verify" | "codes"
  const [enrollData, setEnrollData] = useState(null); // {otpauth_url, qr_svg_base64, secret_for_manual_entry}
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/auth/me/mfa/enroll/start");
        setEnrollData(data);
        setStep("verify");
      } catch (e) {
        toast.error(formatApiError(e));
        onClose();
      }
    })();
  }, [onClose]);

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/me/mfa/enroll/verify", { code });
      setBackupCodes(data.backup_codes);
      setStep("codes");
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Enable two-factor authentication" testid="mfa-enroll-modal">
      {step === "start" && <div className="text-sm text-navy-500 dark:text-navy-300">Loading…</div>}
      {step === "verify" && enrollData && (
        <div className="space-y-4">
          <ol className="text-sm space-y-3 text-navy-700 dark:text-navy-100">
            <li>
              <strong>1.</strong> Scan this QR code with your authenticator app
              <div className="mt-2 inline-block bg-white p-2 rounded border border-navy-200">
                <img
                  src={`data:image/svg+xml;base64,${enrollData.qr_svg_base64}`}
                  alt="MFA QR code"
                  className="w-40 h-40"
                  data-testid="mfa-qr"
                />
              </div>
            </li>
            <li>
              <strong>Or enter manually:</strong>{" "}
              <code className="font-mono text-xs bg-navy-50 dark:bg-navy-900 dark:text-navy-100 px-2 py-0.5 rounded select-all" data-testid="mfa-manual-secret">
                {enrollData.secret_for_manual_entry}
              </code>
            </li>
            <li>
              <strong>2.</strong> Enter the 6-digit code from your app
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="eb-input mt-2 max-w-[160px] font-mono text-center text-lg tracking-widest"
                placeholder="000000"
                data-testid="mfa-verify-code"
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </li>
          </ol>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-navy-200 dark:border-navy-600 dark:text-navy-100">
              Cancel
            </button>
            <button
              onClick={verify}
              disabled={code.length !== 6 || busy}
              className="text-xs px-3 py-1.5 rounded-md bg-navy-900 text-white hover:bg-navy-800 disabled:opacity-50"
              data-testid="mfa-verify-btn"
            >
              {busy ? "Verifying…" : "Verify & enable"}
            </button>
          </div>
        </div>
      )}
      {step === "codes" && (
        <BackupCodesView codes={backupCodes} saved={saved} setSaved={setSaved} onClose={onClose} />
      )}
    </Modal>
  );
}

function BackupCodesView({ codes, saved, setSaved, onClose }) {
  const [copied, setCopied] = useState(false);
  const blob = codes.join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(blob);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = `data:text/plain;charset=utf-8,${encodeURIComponent(blob)}`;
    a.download = `entrybook-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-3" data-testid="mfa-backup-codes-view">
      <p className="text-sm text-navy-700 dark:text-navy-100">
        Save these <strong>{codes.length} backup codes</strong> somewhere safe. Each can be used
        exactly once if you lose access to your authenticator. <strong>This is the only time
        they'll be shown.</strong>
      </p>
      <ul className="grid grid-cols-2 gap-2 font-mono text-sm bg-navy-50 dark:bg-navy-900 rounded p-3 border border-navy-200 dark:border-navy-700">
        {codes.map((c, i) => (
          <li key={i} className="text-navy-900 dark:text-white tracking-wider select-all">{c}</li>
        ))}
      </ul>
      <div className="flex gap-2">
        <button onClick={copy} className="text-xs px-3 py-1.5 rounded-md border border-navy-200 dark:border-navy-600 hover:bg-navy-50 dark:hover:bg-navy-700 dark:text-navy-100 inline-flex items-center gap-1.5">
          {copied ? <><Check className="w-3.5 h-3.5 text-success" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy all</>}
        </button>
        <button onClick={download} className="text-xs px-3 py-1.5 rounded-md border border-navy-200 dark:border-navy-600 hover:bg-navy-50 dark:hover:bg-navy-700 dark:text-navy-100 inline-flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> Download .txt
        </button>
      </div>
      <label className="text-xs flex items-center gap-2 text-navy-700 dark:text-navy-100">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="mfa-saved-check" />
        I've saved these codes somewhere safe
      </label>
      <div className="flex justify-end">
        <button
          onClick={onClose}
          disabled={!saved}
          className="text-xs px-3 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
          data-testid="mfa-codes-done"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------- Disable ------------------------------------------------

function DisableModal({ onClose }) {
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/auth/me/mfa/disable", { current_password: pw, code });
      toast.success("Two-factor authentication disabled");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Disable two-factor authentication" testid="mfa-disable-modal">
      <div className="space-y-3">
        <p className="text-sm text-navy-700 dark:text-navy-100">
          Confirm your password and a current 6-digit code (or a backup code) to disable MFA.
        </p>
        <div>
          <label className="eb-label">Current password</label>
          <input type="password" className="eb-input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="mfa-disable-pw" />
        </div>
        <div>
          <label className="eb-label">Authenticator code or backup code</label>
          <input type="text" className="eb-input font-mono" value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-disable-code" />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-navy-200 dark:border-navy-600 dark:text-navy-100">Cancel</button>
          <button onClick={submit} disabled={!pw || !code || busy} className="text-xs px-3 py-1.5 rounded-md bg-danger text-white hover:bg-danger/90 disabled:opacity-50" data-testid="mfa-disable-confirm">
            {busy ? "Disabling…" : "Disable"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Regenerate ---------------------------------------------

function RegenerateModal({ onClose }) {
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [newCodes, setNewCodes] = useState(null);
  const [saved, setSaved] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/me/mfa/regenerate-backup-codes", {
        current_password: pw, code,
      });
      setNewCodes(data.backup_codes);
      toast.success("Backup codes regenerated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (newCodes) {
    return (
      <Modal onClose={onClose} title="New backup codes" testid="mfa-regen-modal">
        <BackupCodesView codes={newCodes} saved={saved} setSaved={setSaved} onClose={onClose} />
      </Modal>
    );
  }
  return (
    <Modal onClose={onClose} title="Regenerate backup codes" testid="mfa-regen-modal">
      <div className="space-y-3">
        <p className="text-sm text-navy-700 dark:text-navy-100">
          Confirm your password and a current authenticator code. Existing backup codes will be
          replaced.
        </p>
        <div>
          <label className="eb-label">Current password</label>
          <input type="password" className="eb-input" value={pw} onChange={(e) => setPw(e.target.value)} />
        </div>
        <div>
          <label className="eb-label">6-digit authenticator code</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="eb-input font-mono"
            placeholder="000000"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-navy-200 dark:border-navy-600 dark:text-navy-100">Cancel</button>
          <button onClick={submit} disabled={!pw || code.length !== 6 || busy} className="text-xs px-3 py-1.5 rounded-md bg-navy-900 text-white hover:bg-navy-800 disabled:opacity-50">
            {busy ? "Regenerating…" : "Generate new codes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Modal shell --------------------------------------------

function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={testid}>
      <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl shadow-xl w-full max-w-lg">
        <header className="px-5 py-3 border-b border-navy-100 dark:border-navy-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-navy-900 dark:text-white inline-flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-orange-500" /> {title}
          </h2>
          <button onClick={onClose} className="text-navy-400 hover:text-navy-700 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
